import { getMyAccount } from '#api/accounts';
import { usePostCoachGym } from '#api/gyms';
import { Button, Input } from '#atoms';
import { PATH } from '#const/path';
import useSearchGymsQuery from '#hooks/queries/onboard/useSearchGymsQuery';
import { useToast } from '#providers/ToastProvider';
import { useUserInfo } from '#providers/UserInfo';
import { PrevHeaderBarWithTitleTemplate } from '#templates';
import { Gym as TGym } from '#types/gyms';
import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useRecoilState } from 'recoil';
import styled from 'styled-components';
import { gymStateAtom } from '../OnBoardStep/components/Step6/Step6.atoms';
import Gym from './components/Gym';

export function OnBoardGymSearch() {
  const inputRef = useRef<HTMLInputElement>(null);

  const navigate = useNavigate();
  const [gymState, setGymState] = useRecoilState(gymStateAtom);
  const [searchName, setSearchName] = useState('');
  const { data: gyms } = useSearchGymsQuery(searchName);
  const queryClient = useQueryClient();

  const { setUserInfo } = useUserInfo();

  const { showToast } = useToast();

  const { mutate } = usePostCoachGym({
    onSuccess: () => {
      showToast({
        message: '수업 장소가 저장되었습니다.',
        type: 'success'
      });
      getMyAccount().then(account => {
        setUserInfo(account);
        setGymState(account.recommendGyms[0]);
      });
    },
    onError: () => {
      showToast({
        message: '수업 장소를 저장하는데 실패했습니다.',
        type: 'fail'
      });
    }
  });

  const handleClickGym = (gym: TGym) => {
    mutate(gym.id);
    setGymState(gym);
    navigate(-1);
    //navigate(PATH.ONBOARD_GYM);
  };

  useEffect(() => {
    return () => {
      queryClient.invalidateQueries({
        queryKey: ['gyms', 'search']
      });
    };
  }, []);

  const [isMyGym, setIsMyGym] = useState<boolean>(false);
  const [address, setAddress] = useState<string>('');

  const onAddressComplete = (addressData: { address: string }) => {
    setIsMyGym(false);
    setAddress(addressData.address);
    setAddressModalOpen(false);
  };

  const [addressModalOpen, setAddressModalOpen] = useState<boolean>(false);

  useEffect(() => {
    document.body.style.overflowY = addressModalOpen ? 'hidden' : 'auto';
  }, [onAddressComplete, addressModalOpen]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  return (
    <>
      <PrevHeaderBarWithTitleTemplate
        title="수업 장소"
        hasBorder={false}
        ContainerStyle={{ position: 'relative' }}
        defaultBackground
      >
        <Wrapper>
          <Input
            ref={inputRef}
            type="text"
            value={searchName}
            onChange={v => setSearchName(v as string)}
            placeholder="지점 및 센터명으로 검색"
            showDeleteButton
          />
          {searchName !== '' ? (
            gyms && gyms.length > 0 ? (
              <>
                <Count>{gyms.length}개</Count>
                {gyms.map((gym, index) => (
                  <React.Fragment key={gym.id}>
                    <Gym
                      key={gym.id}
                      gym={gym}
                      onClick={handleClickGym.bind(null, gym)}
                    />
                    {index !== gyms.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
                <Wrapper2>
                  <Description>
                    원하는 검색 결과가 없으신가요?
                    <br />
                    장소를 직접 등록해 보세요
                  </Description>
                  <ButtonContainer>
                    <Button onClick={() => navigate(PATH.ONBOARD_GYM)}>
                      등록하기
                    </Button>
                  </ButtonContainer>
                </Wrapper2>
              </>
            ) : (
              <Wrapper2>
                <Description>
                  원하는 검색 결과가 없으신가요?
                  <br />
                  장소를 직접 등록해 보세요
                </Description>
                <ButtonContainer>
                  <Button onClick={() => navigate(PATH.ONBOARD_GYM)}>
                    등록하기
                  </Button>
                </ButtonContainer>
              </Wrapper2>
            )
          ) : (
            <Wrapper2></Wrapper2>
          )}
        </Wrapper>
      </PrevHeaderBarWithTitleTemplate>
    </>
  );
}

const Wrapper = styled.section`
  padding: 20px;
`;

const Wrapper2 = styled.div`
  margin-top: 20px;
  padding: 20px;
  text-align: center;
`;

const Count = styled.div`
  font-weight: 500;
  color: ${({ theme }) => theme.palette.brand300};
  margin-top: 20px;
  margin-bottom: 20px;
`;

const Divider = styled.div`
  margin-top: 20px;
  margin-bottom: 20px;
  height: 1px;
  background-color: ${({ theme }) => theme.palette.gray700};
`;

const Description = styled.span`
  display: block;
  text-align: center;
  font-size: 16px;
  font-weight: 400;
  color: ${({ theme }) => theme.palette.gray100};
  margin-top: 20px;
  margin-bottom: 20px;
`;

const ButtonContainer = styled.div`
  margin-top: 30px;
  left: 0;
  right: 0;
  padding: 0 2rem 40px;
  background-color: var(--color-background);
  max-width: var(--max-width);
  margin: 0 auto;
  width: 40%;
`;
